import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AuthLayout } from "@/pages/Login";
import { Button } from "@/components/ui/Primitives";
import { usePageTitle } from "@/lib/seo";
import { toast } from "sonner";
import { KeyRound, ArrowRight, ArrowLeft, AlertTriangle } from "lucide-react";

/**
 * ResetPassword — /reset?token=... page. User arrives from the email link,
 * types a new password, we POST /auth/reset, then redirect to login.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  usePageTitle("Choose a new password — Central Rentals Canada");

  useEffect(() => {
    if (!token) setError("Missing reset token. Please request a new reset link.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset", { token, new_password: password });
      toast.success("Password updated. Sign in with your new password.");
      nav("/login/landlord", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't reset your password. The link may be expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="You'll be signed in with your new password."
      accent="Password reset"
      accentClass="bg-emerald-50 text-emerald-800 border-emerald-200"
      PortalIcon={KeyRound}
    >
      {!token ? (
        <div className="text-center py-4" data-testid="reset-missing-token">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Missing reset link</h3>
          <p className="text-sm text-slate-600 mb-4">Open the reset link from your email, or request a new one.</p>
          <Link to="/forgot" className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:underline">
            Request a new reset link
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">New password</label>
            <input
              type="password"
              required
              minLength={6}
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              data-testid="reset-password-input"
              className="w-full bg-white border border-slate-200 rounded-md px-3.5 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all shadow-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Confirm new password</label>
            <input
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Retype it"
              data-testid="reset-password-confirm"
              className="w-full bg-white border border-slate-200 rounded-md px-3.5 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all shadow-sm"
            />
          </div>
          {error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded" data-testid="reset-error">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full !bg-emerald-600 hover:!bg-emerald-700 !py-3"
            data-testid="reset-submit"
          >
            {submitting ? "Updating…" : <>Update password <ArrowRight className="w-4 h-4" /></>}
          </Button>
          <div className="text-center pt-2">
            <Link to="/login/landlord" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign-in
            </Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
